import { useState, useCallback, type MutableRefObject } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import {
  formatPremium,
  getPremiumBreakdowns,
  rankIrrAcrossPolicies,
  type RowVisibility,
  type NormalizedPolicy,
} from '@/features/policies-comparison';
import { possessive,
  InsuranceCarrier,
  parseCustomDisclosures,
  getCustomDisclosuresTitle,
  getNylReportDisclosureParagraphs,
  type DisclosureParagraph,
} from '@shared';
import { pdfAuditApi } from '@/api/waterlily';
import { useIsNylPilotAdvisor } from '@/shared/hooks/use-nyl-pilot-advisor';

const COMPARISON_PDF_FILENAME = 'policy-comparison.pdf';

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const PRIMARY = '#7755cc';
const PRIMARY_DARK = '#4b3280';
const PRIMARY_LIGHT = '#f0ebf8';

const CARRIER_MARKS: Partial<Record<InsuranceCarrier, { src: string; size: number }>> = {
  [InsuranceCarrier.CareScout]: { src: '/logos/carescout-mark.svg', size: 5 },
  [InsuranceCarrier.LincolnFinancial]: { src: '/logos/lincoln-mark.svg', size: 6 },
  [InsuranceCarrier.SecurianFinancial]: { src: '/logos/securian financial.svg', size: 6 },
  [InsuranceCarrier.Nationwide]: { src: '/logos/nationwide.svg', size: 6 },
  [InsuranceCarrier.OneAmerica]: { src: '/logos/oneamerica.svg', size: 6 },
};

function imageToDataUrl(imageUrl: string, size: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;
      const scale = Math.min(size / img.naturalWidth, size / img.naturalHeight);
      const w = img.naturalWidth * scale;
      const h = img.naturalHeight * scale;
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = imageUrl;
  });
}

interface CarrierLogo {
  dataUrl: string;
  size: number;
}

async function preloadCarrierLogos(
  policies: NormalizedPolicy[],
): Promise<Map<number, CarrierLogo>> {
  const logoMap = new Map<number, CarrierLogo>();
  const seen = new Set<number>();

  const tasks: Promise<void>[] = [];
  for (const policy of policies) {
    const carrierId = policy.carrierId;
    if (carrierId == null || seen.has(carrierId)) continue;
    seen.add(carrierId);
    const mark = CARRIER_MARKS[carrierId as InsuranceCarrier];
    if (!mark) continue;
    tasks.push(
      imageToDataUrl(mark.src, 120).then(dataUrl => {
        logoMap.set(carrierId, { dataUrl, size: mark.size });
      }),
    );
  }

  await Promise.allSettled(tasks);
  return logoMap;
}

interface ToggleableColumnDef {
  key: keyof RowVisibility;
  header: string;
  getValue: (policy: NormalizedPolicy, inflatedBenefitLabel: string) => string;
}

interface FixedColumnDef {
  header: string;
  getValue: (policy: NormalizedPolicy, inflatedBenefitLabel: string) => string;
}

function getToggleableColumnDefs(): ToggleableColumnDef[] {
  return [
    {
      key: 'monthlyBenefit',
      header: 'Monthly Benefit',
      getValue: (policy, inflatedLabel) =>
        `Day One: ${policy.initialMonthlyBenefit ?? '-'}\n${inflatedLabel}: ${policy.inflatedMonthlyBenefit ?? '-'}`,
    },
    {
      key: 'totalBenefit',
      header: 'Total Benefit',
      getValue: (policy, inflatedLabel) =>
        `Day One: ${policy.initialTotalBenefit ?? '-'}\n${inflatedLabel}: ${policy.inflatedTotalBenefit ?? '-'}`,
    },
    {
      key: 'growth',
      header: 'Growth',
      getValue: policy => policy.inflationDescription ?? '-',
    },
    {
      key: 'eliminationPeriod',
      header: 'Elim. Period',
      getValue: policy => policy.eliminationPeriodLabel ?? '-',
    },
    {
      key: 'policyType',
      header: 'Type',
      getValue: policy => policy.productDescription ?? '-',
    },
    {
      key: 'methodBenefitsPaid',
      header: 'Benefit Method',
      getValue: policy => policy.paymentMethod ?? '-',
    },
    {
      key: 'irr',
      header: 'IRR for Modeled LTC Event',
      getValue: () => '',
    },
    {
      key: 'premiumGuarantee',
      header: 'Premium Guarantee',
      getValue: policy => {
        const val = policy.premiumGuaranteed;
        if (!val) return '-';
        if (val.toLowerCase().includes('not guaranteed')) return 'Not Guaranteed';
        if (val.toLowerCase().includes('guaranteed')) return 'Guaranteed';
        return val;
      },
    },
  ];
}

function getFixedColumnDefs(): FixedColumnDef[] {
  return [
    {
      header: 'Premium',
      getValue: policy => formatPremium(policy),
    },
  ];
}

interface PdfMetadata {
  clientName: string;
  advisorName: string;
  organizationName?: string;
  customDisclosures?: string;
  isNylPilotAdvisor?: boolean;
}

interface DownloadPdfParams {
  orderedData: NormalizedPolicy[];
  visibleRows: RowVisibility;
  inflatedBenefitLabel: string;
  nycLifeDemo?: boolean;
  metadata: PdfMetadata;
}

function formatPdfDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function drawPdfHeader(pdf: jsPDF, metadata: PdfMetadata, margin: number): number {
  const { clientName, advisorName, organizationName } = metadata;
  let y = 14;

  if (organizationName) {
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(PRIMARY_DARK);
    pdf.text(organizationName, margin, y);
    y += 3.5;
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(130, 130, 130);
    pdf.text('powered by Waterlily', margin, y);
    y += 9;
  }

  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(PRIMARY_DARK);
  pdf.text('Policy Comparison', margin, y);
  y += 8;

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(100, 100, 100);

  const details: string[] = [];
  if (clientName) details.push(`Client: ${clientName}`);
  if (advisorName) details.push(`Advisor: ${advisorName}`);
  details.push(`Date: ${formatPdfDate(new Date())}`);

  pdf.text(details.join('   |   '), margin, y);
  y += 4;

  pdf.setDrawColor(PRIMARY);
  pdf.setLineWidth(0.5);
  pdf.line(margin, y, pdf.internal.pageSize.getWidth() - margin, y);
  y += 6;

  return y;
}

const DISCLOSURE_TITLE_FONT_SIZE = 9;
const DISCLOSURE_TEXT_FONT_SIZE = 7.5;
const DISCLOSURE_LINE_HEIGHT = 3.5;
const DISCLOSURE_PARAGRAPH_GAP = 3;
const DISCLOSURE_BOX_PADDING_X = 6;
const DISCLOSURE_BOX_PADDING_TOP = 5;
const DISCLOSURE_BOX_PADDING_BOTTOM = 5;
const DISCLOSURE_TITLE_HEIGHT = DISCLOSURE_TITLE_FONT_SIZE * 0.35 + 3;

type SegmentedLine = Array<{ text: string; url?: string }>;

function wrapParagraphSegments(
  pdf: jsPDF,
  paragraph: DisclosureParagraph,
  textW: number,
): SegmentedLine[] {
  const lines: SegmentedLine[] = [[]];
  let remaining = textW;

  for (const segment of paragraph) {
    const segmentUrl = segment.type === 'link' ? segment.url : undefined;
    const words = segment.content.split(/(\s+)/).filter(Boolean);
    for (const word of words) {
      const width = pdf.getTextWidth(word);
      const current = lines[lines.length - 1];
      const isWhitespaceOnly = /^\s+$/.test(word);
      if (width > remaining && !(isWhitespaceOnly && current.length === 0)) {
        lines.push([]);
        remaining = textW;
      }
      const line = lines[lines.length - 1];
      const last = line[line.length - 1];
      if (last && last.url === segmentUrl && !isWhitespaceOnly) {
        last.text += word;
      } else if (last && last.url === segmentUrl && isWhitespaceOnly && line.length > 0) {
        last.text += word;
      } else {
        line.push(segmentUrl ? { text: word, url: segmentUrl } : { text: word });
      }
      remaining -= width;
    }
  }

  return lines.filter(line => line.some(run => run.text.trim().length > 0));
}

function drawDisclosureBox(
  pdf: jsPDF,
  title: string,
  paragraphLines: SegmentedLine[][],
  margin: number,
  startY: number,
  contentW: number,
  boxHeight: number,
) {
  const boxX = margin;
  const boxY = startY;
  const cornerRadius = 3;

  pdf.setFillColor(240, 235, 248);
  pdf.setDrawColor(210, 200, 230);
  pdf.setLineWidth(0.15);
  pdf.roundedRect(boxX, boxY, contentW, boxHeight, cornerRadius, cornerRadius, 'FD');

  let cursorY = boxY + DISCLOSURE_BOX_PADDING_TOP;

  pdf.setFontSize(DISCLOSURE_TITLE_FONT_SIZE);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(PRIMARY);
  pdf.text(title.toUpperCase(), boxX + DISCLOSURE_BOX_PADDING_X, cursorY + DISCLOSURE_TITLE_FONT_SIZE * 0.35);
  cursorY += DISCLOSURE_TITLE_HEIGHT;

  pdf.setFontSize(DISCLOSURE_TEXT_FONT_SIZE);
  pdf.setFont('helvetica', 'normal');

  paragraphLines.forEach(lines => {
    lines.forEach(line => {
      let x = boxX + DISCLOSURE_BOX_PADDING_X;
      const baseline = cursorY + DISCLOSURE_TEXT_FONT_SIZE * 0.35;
      line.forEach(run => {
        if (run.url) {
          pdf.setTextColor(PRIMARY);
          pdf.textWithLink(run.text, x, baseline, { url: run.url });
        } else {
          pdf.setTextColor(107, 114, 128);
          pdf.text(run.text, x, baseline);
        }
        x += pdf.getTextWidth(run.text);
      });
      cursorY += DISCLOSURE_LINE_HEIGHT;
    });
    cursorY += DISCLOSURE_PARAGRAPH_GAP;
  });
}

function computeBoxHeight(paragraphLines: SegmentedLine[][]): number {
  const textHeight = paragraphLines.reduce(
    (sum, lines) => sum + lines.length * DISCLOSURE_LINE_HEIGHT + DISCLOSURE_PARAGRAPH_GAP,
    0,
  );
  return DISCLOSURE_BOX_PADDING_TOP + DISCLOSURE_TITLE_HEIGHT + textHeight + DISCLOSURE_BOX_PADDING_BOTTOM;
}

function drawDisclosures(
  pdf: jsPDF,
  clientName: string,
  margin: number,
  customDisclosuresHtml?: string,
  organizationName?: string,
  isNylPilotAdvisor?: boolean,
) {
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const contentW = pageW - margin * 2;
  const textW = contentW - 12;
  const footerReserve = 14;
  const gapAboveBox = 8;
  const gapBetweenBoxes = 4;

  const firstName = clientName.split(' ')[0] || clientName;

  const standardParagraphs: DisclosureParagraph[] = [
    [
      {
        type: 'text',
        content: `${possessive(firstName)} projected LTC cost and age they will need LTC are predictions from models trained on other families' LTC journeys. These predictions are estimates and are not guaranteed. ${possessive(firstName)} LTC costs may be higher or lower, and may begin sooner or later than what is shown here. There is no guarantee that a plan built on these numbers will cover ${possessive(firstName)} costs.`,
      },
    ],
    [
      {
        type: 'text',
        content:
          'The investing and insurance information provided on this page is for educational purposes only. Waterlily Caregiving, Inc. does not offer advisory or brokerage services, nor does it recommend or advise users to buy or sell particular stocks, securities insurance policies, or other financial products. Any information entered about an insurance policy is a subset of policy terms and can only be used to show an estimate of costs covered by a policy. Any policy will likely have terms that limit coverage beyond what is considered here.',
      },
    ],
    [
      { type: 'text', content: 'Additional disclaimers and disclosures on our models can be found on our site: ' },
      {
        type: 'link',
        content: 'waterlily.com/planning-for-yourself/disclosures-disclaimers',
        url: 'https://www.waterlily.com/planning-for-yourself/disclosures-disclaimers',
      },
      { type: 'text', content: '.' },
    ],
  ];
  const waterlilyParagraphs = isNylPilotAdvisor
    ? getNylReportDisclosureParagraphs(firstName)
    : standardParagraphs;

  const customParagraphs = parseCustomDisclosures(customDisclosuresHtml);

  pdf.setFontSize(DISCLOSURE_TEXT_FONT_SIZE);
  pdf.setFont('helvetica', 'normal');

  const waterlilyLines = waterlilyParagraphs.map(p => wrapParagraphSegments(pdf, p, textW));
  const waterlilyHeight = computeBoxHeight(waterlilyLines);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lastTableY = (pdf as any).lastAutoTable?.finalY ?? margin;
  const spaceBelow = pageH - lastTableY - footerReserve;

  let startY: number;
  if (spaceBelow >= waterlilyHeight + gapAboveBox) {
    startY = lastTableY + gapAboveBox;
  } else {
    pdf.addPage();
    startY = margin + gapAboveBox;
  }

  drawDisclosureBox(pdf, 'Disclosures', waterlilyLines, margin, startY, contentW, waterlilyHeight);

  if (customParagraphs.length === 0) return;

  const customLines = customParagraphs.map(p => wrapParagraphSegments(pdf, p, textW));
  const customHeight = computeBoxHeight(customLines);
  const customTitle = getCustomDisclosuresTitle(organizationName);

  const nextStartY = startY + waterlilyHeight + gapBetweenBoxes;
  const spaceRemaining = pageH - nextStartY - footerReserve;

  let customStartY: number;
  if (spaceRemaining >= customHeight) {
    customStartY = nextStartY;
  } else {
    pdf.addPage();
    customStartY = margin + gapAboveBox;
  }

  drawDisclosureBox(pdf, customTitle, customLines, margin, customStartY, contentW, customHeight);
}

async function buildPdf({
  orderedData,
  visibleRows,
  inflatedBenefitLabel,
  nycLifeDemo,
  metadata,
}: DownloadPdfParams) {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const margin = 10;
  const tableStartY = drawPdfHeader(pdf, metadata, margin);
  const logoMap = nycLifeDemo ? null : await preloadCarrierLogos(orderedData);

  const NYL_LOGO_SIZE = 6;
  const nylLogo = nycLifeDemo
    ? await imageToDataUrl('/logos/nyl-logo.png', 120).catch(() => null)
    : null;

  const toggleableCols = getToggleableColumnDefs().filter(colDef => visibleRows[colDef.key]);
  const fixedCols = getFixedColumnDefs();
  const activeCols = [...toggleableCols, ...fixedCols];

  const allBreakdowns = orderedData.map(p => getPremiumBreakdowns(p));
  rankIrrAcrossPolicies(allBreakdowns);

  const irrColIndex = activeCols.findIndex(col => 'key' in col && col.key === 'irr');

  const head = [['Product', ...activeCols.map(colDef => colDef.header)]];

  const body = orderedData.map((policy, rowIndex) => {
    const row = [
      nycLifeDemo ? 'Insurance' : (policy.productName ?? 'Insurance'),
      ...activeCols.map(colDef => colDef.getValue(policy, inflatedBenefitLabel)),
    ];

    if (irrColIndex !== -1) {
      const breakdowns = allBreakdowns[rowIndex];
      const lines = breakdowns
        .filter(b => b.irrFormatted)
        .map(b => {
          const badge =
            b.irrRank === 1 ? '(Highest IRR)' : b.irrRank === 2 ? '(Second Highest IRR)' : '';
          return badge ? `${b.irrFormatted}\n${badge}` : b.irrFormatted!;
        });
      row[irrColIndex + 1] = lines.length > 0 ? lines.join('\n') : '-';
    }

    return row;
  });

  pdf.setLineHeightFactor(1.35);

  const narrowColumnWidths: Record<string, number> = {
    policyType: 28,
    premium: 32,
    premiumGuarantee: 20,
    methodBenefitsPaid: 26,
    growth: 22,
    eliminationPeriod: 24,
  };

  const maxLogoSize = nylLogo
    ? NYL_LOGO_SIZE
    : orderedData.reduce((max, p) => {
        if (p.carrierId == null) return max;
        const logo = logoMap!.get(p.carrierId);
        return logo ? Math.max(max, logo.size) : max;
      }, 0);
  const logoGap = 1.5;

  const columnStyles: Record<number, object> = {
    0: {
      fontStyle: 'bold',
      halign: 'center',
      cellWidth: 32,
      cellPadding:
        maxLogoSize > 0
          ? { top: maxLogoSize + logoGap + 2, right: 2, bottom: 2, left: 2 }
          : undefined,
    },
  };

  activeCols.forEach((col, i) => {
    const colIndex = i + 1;
    const key = 'key' in col ? (col as ToggleableColumnDef).key : 'premium';
    const width = narrowColumnWidths[key];
    if (width) {
      columnStyles[colIndex] = { cellWidth: width };
    }
  });

  autoTable(pdf, {
    head,
    body,
    startY: tableStartY,
    margin: { left: margin, right: margin, bottom: 16 },
    theme: 'grid',
    tableWidth: 'auto',
    rowPageBreak: 'avoid',
    styles: {
      fontSize: 7,
      cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
      minCellHeight: 8,
      textColor: PRIMARY_DARK,
      lineColor: [210, 200, 230],
      lineWidth: 0.25,
      valign: 'middle',
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: PRIMARY,
      textColor: '#ffffff',
      fontStyle: 'bold',
      fontSize: 7.5,
      halign: 'center',
      lineColor: PRIMARY,
      lineWidth: 0.25,
    },
    bodyStyles: {
      halign: 'center',
    },
    columnStyles,
    alternateRowStyles: {
      fillColor: PRIMARY_LIGHT,
    },
    didDrawCell: data => {
      if (data.section !== 'body' || data.column.index !== 0) return;

      let logoDataUrl: string | undefined;
      let logoSize: number;

      if (nylLogo) {
        logoDataUrl = nylLogo;
        logoSize = NYL_LOGO_SIZE;
      } else {
        const policy = orderedData[data.row.index];
        if (!policy?.carrierId) return;
        const logo = logoMap!.get(policy.carrierId);
        if (!logo) return;
        logoDataUrl = logo.dataUrl;
        logoSize = logo.size;
      }

      const cellX = data.cell.x;
      const cellY = data.cell.y;
      const cellW = data.cell.width;
      const imgX = cellX + (cellW - logoSize) / 2;
      const imgY = cellY + 2;

      pdf.addImage(logoDataUrl, 'PNG', imgX, imgY, logoSize, logoSize);
    },
  });

  drawDisclosures(
    pdf,
    metadata.clientName,
    margin,
    metadata.customDisclosures,
    metadata.organizationName,
    metadata.isNylPilotAdvisor,
  );

  const totalPages = pdf.getNumberOfPages();
  const pageH = pdf.internal.pageSize.getHeight();
  const pageW = pdf.internal.pageSize.getWidth();

  const footerLabel = metadata.organizationName
    ? `Policy Comparison - ${metadata.organizationName} | Powered By Waterlily`
    : 'Policy Comparison - Powered By Waterlily';

  for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
    pdf.setPage(pageNum);
    pdf.setFontSize(7);
    pdf.setTextColor(150, 150, 150);
    pdf.text(footerLabel, margin, pageH - 8);
    pdf.text(`Page ${pageNum} of ${totalPages}`, pageW - margin, pageH - 8, { align: 'right' });
  }

  return pdf.output('blob');
}

interface UseDownloadComparisonPdfParams {
  orderedDataRef: MutableRefObject<NormalizedPolicy[]>;
  inflatedBenefitLabel: string;
  nycLifeDemo?: boolean;
  clientId?: string;
  clientName: string;
  advisorName: string;
  organizationName?: string;
  customDisclosures?: string;
}

export function useDownloadComparisonPdf({
  orderedDataRef,
  inflatedBenefitLabel,
  nycLifeDemo,
  clientId,
  clientName,
  advisorName,
  organizationName,
  customDisclosures,
}: UseDownloadComparisonPdfParams) {
  const [isPending, setIsPending] = useState(false);
  const isNylPilotAdvisor = useIsNylPilotAdvisor();

  const downloadPdf = useCallback(
    async (visibleRows: RowVisibility) => {
      if (isPending) return;
      setIsPending(true);

      try {
        const blob = await buildPdf({
          orderedData: orderedDataRef.current,
          visibleRows,
          inflatedBenefitLabel,
          nycLifeDemo,
          metadata: {
            clientName,
            advisorName,
            organizationName,
            customDisclosures,
            isNylPilotAdvisor,
          },
        });
        triggerBrowserDownload(blob, COMPARISON_PDF_FILENAME);

        if (clientId) {
          try {
            const pdfBase64 = await blobToBase64(blob);
            await pdfAuditApi.log(clientId, {
              pdfBase64,
              pdfType: 'policy_comparison',
              action: 'download',
              filename: COMPARISON_PDF_FILENAME,
            });
          } catch (auditErr) {
            console.error('Failed to record policy comparison download audit log', auditErr);
          }
        }
      } finally {
        setIsPending(false);
      }
    },
    [
      isPending,
      orderedDataRef,
      inflatedBenefitLabel,
      nycLifeDemo,
      clientId,
      clientName,
      advisorName,
      organizationName,
      customDisclosures,
      isNylPilotAdvisor,
    ],
  );

  return { downloadPdf, isPending };
}
